module ContractPageModel

open FSharpPlus
open PortalLibs.Client.Components
open PortalLibs.Client
open Shared
open Model
open Elmish
open Pages
open Elmish.Navigation
open Elmish.Bridge
open Browser
open System
open PortalLibs.Shared.NewTable
open PortalLibs.Shared
open Shared.Models

type Tab =
  | Overview
  | ChangeOrders
  | ARAndCashFlow
  | CostOfConstruction
  | CostAndBilling
  | ContractProfitFadeTab
  | AverageRate
  | PurchaseOrders
  | AI
  | Scheduling

type PhaseCode = string

type ExpansionRequest<'T> =
  | Expand of 'T
  | Collapse of 'T


type Model =
  { SearchValues: Map<JobCostId, string option>
    TableStates: Map<JobCostId, NewTableNext.Model<JobCostDetail, JobCostDetailField, unit>>
    SelectedContractId: int64 option
    SelectedTab: Tab
    ChangeOrderHeaders: Map<ContractId, ChangeOrderHeader list>
    ChangeOrderItems: Map<HeaderId, ChangeOrderItem list>
    ChangeOrderItemLines: Map<ItemId, ChangeOrderLine list>
    JobHistories: Map<ContractId, JobHistory list>
    JobForecasts: Map<ContractId, JobForecast list>
    JobSchedules: Map<ContractId, JobSchedule option>
    ContractARBuckets: Map<ContractId, ContractARBucket list>
    ContractProfitFades: Map<ContractId, ContractProfitFade option * ContractProfit list>
    AIJobHistories: Map<ContractId, JobHistory list>
    JobCosts: Map<ContractId, JobPhaseCosts list>
    JobCostSubs: Map<JobCostId, JobCostSub list>
    JobCostDetails: Map<JobCostId, JobCostDetail list>
    JobCostDetailAttachments: Map<JobCostId, Map<JobCostDetailId, JobCostDetailAttachment list>>
    JobCostDetailAttachmentFiles: Map<JobCostAttachmentId, JobCostAttachmentContent>
    JobCostDetailAttachmentFilesVisited: Map<JobCostAttachmentId, unit>
    JobCostAttachmentPreview: JobCostAttachmentId option
    JobCostAttachmentDownload: JobCostDetailAttachment option
    ExpandedCostOfConstructionPhases: Map<ContractId, PhaseCode list>
    ExpandedCostOfConstructionDetails: Map<JobCostDetailId, unit>
    JobCostAttachmentsModalOpen: (JobCostId * JobCostDetailId) option
    PurchaseOrders: Map<ContractId, PurchaseOrder list>
    PurchaseOrderRequest: PurchaseOrderRequest option }

type Msg =
  | SearchValueChanged of JobCostId * string option
  | TableStateChanged of JobCostId * NewTableNext.Msg<JobCostDetail, JobCostDetailField, unit>
  | ContractSelected of ContractId option
  | TabSelected of Tab
  | ChangeOrderHeadersLoaded of ContractId * ChangeOrderHeader list
  | ChangeOrderItemsLoaded of HeaderId * ChangeOrderItem list
  | ChangeOrderItemLinesLoaded of ItemId * ChangeOrderLine list
  | JobHistories of ContractId * JobHistory list
  | JobForecasts of ContractId * JobForecast list
  | JobSchedule of ContractId * JobSchedule option
  | ContractProfitFade of ContractId * ContractProfitFade option * ContractProfit list
  | JobCosts of {| ContractId: ContractId; Jobs: JobPhaseCosts list |}
  | JobCostSubs of List<JobCostId * JobCostSub list>
  | JobCostDetails of JobCostId * JobCostDetail list
  | JobCostDetailsCount of JobCostId * int64
  | JobCostDetailsTotal of JobCostId * JobCostDetailField * float
  | JobCostDetailAttachments of JobCostId * Map<JobCostDetailId, JobCostDetailAttachment list>
  | BillingRequested
  | CostOfConstructionPhaseExpansionToggled of ContractId * PhaseCode
  | CostOfConstructionDetailsExpansion of ExpansionRequest<JobCostId>
  | ShowAttachmentsModal of (JobCostId * JobCostDetailId) option
  | PreviewAttachment of JobCostAttachmentId option
  | DownloadAttachment of JobCostDetailAttachment option
  | JobCostDetailAttachmentContent of JobCostAttachmentId * JobCostAttachmentContent
  | ExecuteCostDetailSearch of JobCostId * SearchType
  | RequestPurchaseOrderContractId of ContractId option
  | PurchaseOrders of ContractId * PurchaseOrder list
  | ARBuckets of ContractId * ContractARBucket list
  | MassPurchaseOrders of (ContractId * PurchaseOrder list) list
  | PurchaseOrderRequestChanged of PurchaseOrderRequest option
  | SendPurchaseOrderRequest of PurchaseOrderRequest option

let init () =
  { SearchValues = Map.empty
    TableStates = Map.empty
    SelectedContractId = None
    SelectedTab = Overview
    ChangeOrderHeaders = Map.empty
    ChangeOrderItems = Map.empty
    ChangeOrderItemLines = Map.empty
    JobHistories = Map.empty
    JobForecasts = Map.empty
    JobSchedules = Map.empty
    ContractProfitFades = Map.empty
    ContractARBuckets = Map.empty
    AIJobHistories = Map.empty
    JobCosts = Map.empty
    JobCostSubs = Map.empty
    JobCostDetails = Map.empty
    JobCostDetailAttachments = Map.empty
    JobCostDetailAttachmentFiles = Map.empty
    JobCostDetailAttachmentFilesVisited = Map.empty
    JobCostAttachmentPreview = None
    JobCostAttachmentDownload = None
    ExpandedCostOfConstructionPhases = Map.empty
    ExpandedCostOfConstructionDetails = Map.empty
    JobCostAttachmentsModalOpen = None
    PurchaseOrders = Map.empty
    PurchaseOrderRequest = None },
  Cmd.none

let randomAiJobHistories contractsOpt contractId (jobHistories: JobHistory list) aiInfos =
  let rnd = Random()
  let aiInfoOpt = Map.tryFind contractId aiInfos

  let lastHistoryOpt =
    jobHistories
    |> List.sortBy (fun s -> s.Date)
    |> List.tryLast

  let jobOpt =
    contractsOpt
    >>= Map.tryFind (contractId.ToString())

  match aiInfoOpt, lastHistoryOpt, jobOpt with
  | Some aiInfo, Some lastHistory, Some contract ->
    match contract.EstCompletion with
    | None -> []
    | Some estCompletionDate ->
      let weeks =
        ((estCompletionDate - lastHistory.Date).TotalDays
         / 7.0)

      [ 0 .. rnd.Next(
          int (
            weeks * 0.7
            + weeks * 0.2 * (1.0 - aiInfo.SuccessRate)
          ),
          int (weeks + weeks * 2.0 * (1.0 - aiInfo.SuccessRate))
        ) ]
      |> List.fold
           (fun output week ->
             match List.tryLast output with
             | None ->
               [ { Id = 0L
                   ContractId = contract.Id
                   Date = lastHistory.Date.AddDays((week * 7) |> float)
                   Cost = lastHistory.Cost
                   Billing = lastHistory.Billing } ]
             | Some last ->
               ({ Id = 0L
                  ContractId = contract.Id
                  Date = lastHistory.Date.AddDays((week * 7) |> float)
                  Cost =
                    Math.Round(
                      (float (rnd.Next(0, 2000))
                       * 10.0
                       * (1.2 - aiInfo.SuccessRate))
                      + last.Cost,
                      0
                    )
                  Billing =
                    Math.Round(
                      (float (rnd.Next(0, 2000))
                       * 10.0
                       * (aiInfo.SuccessRate + 0.2))
                      + last.Billing,
                      0
                    ) }
                :: (output |> List.rev))
               |> List.rev)
           []
  | _, _, _ -> []


let searchValue searchModels costId =
  searchModels |> Map.tryFind costId >>= id

let tableState tableStates costId =
  tableStates
  |> Map.tryFind costId
  |> Option.defaultValue { NewTableNext.init None with Sort = (JobCostDetailField.PostedDate |> Desc |> Some) }

let update msg model contracts aiInfos =
  match msg with
  | ExecuteCostDetailSearch (costId, searchType) ->
    let searchValue = searchValue model.SearchValues costId
    let tableState = tableState model.TableStates costId

    let searchParams =
      { Query =
          [ match searchValue |> Option.noneIfEmptyStrOptTrim with
            | None -> ()
            | Some input ->
              { Field = JobCostDetailField.Description
                Query = Wildcard(input) } ]
        Page = tableState.Page
        PageSize = tableState.PageSize
        Sort = tableState.Sort
        PageCount = false
        Totals = [ Units; Hours; Cost ] }

    match searchType with
    | Search ->
      let searchParamsPageReset =
        { searchParams with
            Page = 0
            PageCount = true }

      (costId, searchParamsPageReset)
      |> RSJobCostDetails
      |> Bridge.Send

      { model with TableStates = Map.add costId (tableState |> NewTableNext.resetModel) model.TableStates },
      Cmd.none
    | TableStateChange ->
      (costId, searchParams)
      |> RSJobCostDetails
      |> Bridge.Send

      model, Cmd.none
  | PurchaseOrders (contractId, purchaseOrders) ->
    { model with PurchaseOrders = Map.add contractId purchaseOrders model.PurchaseOrders }, Cmd.none
  | ARBuckets (contractId, buckets) ->
    { model with ContractARBuckets = Map.add contractId buckets model.ContractARBuckets }, Cmd.none
  | MassPurchaseOrders allPurchaseOrders ->
    { model with
        PurchaseOrders =
          List.fold
            (fun purchaseOrders (contractId, contractsPurchaseOrders) ->
              Map.add contractId contractsPurchaseOrders purchaseOrders)
            model.PurchaseOrders
            allPurchaseOrders },
    Cmd.none
  | SearchValueChanged (costId, searchValue) ->
    { model with SearchValues = Map.add costId searchValue model.SearchValues },
    Cmd.ofMsg (ExecuteCostDetailSearch(costId, Search))
  | TableStateChanged (costId, msg) ->
    let tableModel = tableState model.TableStates costId
    let newModel, newCmd, externalMsg = NewTableNext.update msg tableModel

    { model with TableStates = Map.add costId newModel model.TableStates },
    Cmd.batch [
      Cmd.map (fun msg -> TableStateChanged(costId, msg)) newCmd
      match externalMsg with
      | NewTableNext.PagingOrSortChanged _ -> Cmd.ofMsg (ExecuteCostDetailSearch(costId, TableStateChange))
      | _ -> Cmd.none
    ]
  | ContractSelected contractId ->
    let route =
      match contractId with
      | None -> ""
      | Some id -> string id

    model,
    route
    |> Page.Contract
    |> toPath
    |> Navigation.newUrl
  | TabSelected tab ->
    { model with
        SelectedTab = tab
        JobCostAttachmentsModalOpen = None },
    Cmd.none
  | ChangeOrderHeadersLoaded (contractId, orders) ->
    { model with ChangeOrderHeaders = Map.add contractId orders model.ChangeOrderHeaders }, Cmd.none
  | ChangeOrderItemsLoaded (orderId, items) ->
    { model with ChangeOrderItems = Map.add orderId items model.ChangeOrderItems }, Cmd.none
  | ChangeOrderItemLinesLoaded (itemId, lines) ->
    { model with ChangeOrderItemLines = Map.add itemId lines model.ChangeOrderItemLines }, Cmd.none
  | JobHistories (contractId, histories) ->
    { model with
        JobHistories = Map.add contractId histories model.JobHistories
        AIJobHistories =
          Map.add contractId (randomAiJobHistories contracts contractId histories aiInfos) model.AIJobHistories },
    Cmd.none
  | JobForecasts (contractId, forecasts) ->
    { model with
        JobForecasts = Map.add contractId forecasts model.JobForecasts },
    Cmd.none
  | JobSchedule (contractId, schedule) ->
    { model with
        JobSchedules = Map.add contractId schedule model.JobSchedules },
    Cmd.none
  | JobCosts v -> { model with JobCosts = Map.add v.ContractId v.Jobs model.JobCosts }, Cmd.none
  | ContractProfitFade (contractId, fade, jobProfitList) ->
    { model with ContractProfitFades = Map.add contractId (fade, jobProfitList) model.ContractProfitFades },
    Cmd.none
  | JobCostSubs costs ->
    { model with JobCostSubs = model.JobCostSubs |> Map.addRange costs }, Cmd.none
  | JobCostDetails (costId, details) ->
    { model with
        JobCostDetails = Map.add costId details model.JobCostDetails
        TableStates =
          Map.tryFind costId model.TableStates
          |> Option.map (fun state -> Map.add costId { state with IsLoading = false } model.TableStates)
          |> Option.defaultValue model.TableStates },
    Cmd.none
  | JobCostDetailsCount (costId, count) ->
    { model with
        TableStates =
          Map.tryFind costId model.TableStates
          |> Option.map (fun state -> Map.add costId { state with TotalItems = count } model.TableStates)
          |> Option.defaultValue model.TableStates },
    Cmd.none
  | JobCostDetailsTotal (costId, field, total) ->
    { model with
        TableStates =
          Map.tryFind costId model.TableStates
          |> Option.map (fun state ->
            Map.add costId { state with Totals = Map.add field total state.Totals } model.TableStates)
          |> Option.defaultValue model.TableStates },
    Cmd.none
  | JobCostDetailAttachments (costId, attachments) ->
    { model with
        JobCostDetailAttachments =
          model.JobCostDetailAttachments
          |> Map.add costId attachments },
    Cmd.none
  | JobCostDetailAttachmentContent (attachmentId, content) ->
    match model.JobCostAttachmentDownload, content with
    | Some attachment, Downloaded ->
      if attachment.Id = attachmentId then
        let fileUrl =
          (sprintf
            "/attachments/%s/%s"
            (attachment.Id.ToString())
            (attachment.OrigFileName |> Option.emptyStrIfNone))

        window.location.href <- fileUrl
    | _, _ -> ()

    { model with
        JobCostDetailAttachmentFiles =
          model.JobCostDetailAttachmentFiles
          |> Map.add attachmentId content
        JobCostAttachmentDownload =
          match model.JobCostAttachmentDownload with
          | Some attachment ->
            if attachment.Id = attachmentId then
              match content with
              | Downloaded -> None
              | VistaNotAvailable -> model.JobCostAttachmentDownload
            else
              model.JobCostAttachmentDownload
          | None -> model.JobCostAttachmentDownload },
    Cmd.none
  | ShowAttachmentsModal ids ->
    if Option.isSome ids then
      Util.bodyScrollDisable ()
    else
      Util.bodyScrollEnable ()

    { model with
        JobCostAttachmentsModalOpen = ids
        JobCostDetailAttachmentFilesVisited = Map.empty
        JobCostAttachmentPreview = None },
    match ids with
    | None -> Cmd.none
    | Some (jobCostId, jobCostDetailId) ->
      model.JobCostDetailAttachments
      |> Map.tryFind jobCostId
      |> Option.defaultValue Map.empty
      |> Map.tryFind jobCostDetailId
      |> Option.defaultValue []
      |> (fun attachments ->
        match attachments with
        | [ attachment ] -> Cmd.ofMsg (attachment.Id |> Some |> PreviewAttachment)
        | _ -> Cmd.none)
  | BillingRequested ->
    match model.SelectedContractId with
    | Some id -> id |> RSContractBillingRequested |> Bridge.Send
    | None -> ()

    model, Cmd.none
  | PreviewAttachment attachmentIdOpt ->
    match attachmentIdOpt with
    | Some attachmentId ->
      attachmentId |> RSJobCostAttachment |> Bridge.Send
      Util.scrollIntoView "ModalCardBody"
    | None -> ()

    { model with
        JobCostAttachmentPreview = attachmentIdOpt
        JobCostAttachmentDownload = None
        JobCostDetailAttachmentFilesVisited =
          match attachmentIdOpt with
          | Some attachmentId ->
            (model.JobCostDetailAttachmentFilesVisited
             |> Map.add attachmentId ())
          | None -> model.JobCostDetailAttachmentFilesVisited
        JobCostDetailAttachmentFiles =
          match attachmentIdOpt with
          | Some attachmentId ->
            match Map.tryFind attachmentId model.JobCostDetailAttachmentFiles with
            | None
            | Some (VistaNotAvailable _) -> Map.remove attachmentId model.JobCostDetailAttachmentFiles
            | Some _ -> model.JobCostDetailAttachmentFiles
          | None -> model.JobCostDetailAttachmentFiles },
    Cmd.none
  | DownloadAttachment attachmentIdOpt ->
    match attachmentIdOpt with
    | Some attachment ->
      attachment.Id
      |> RSJobCostAttachment
      |> Bridge.Send

      Util.scrollIntoView "ModalCardBody"
    | None -> ()

    { model with
        JobCostAttachmentDownload = attachmentIdOpt
        JobCostAttachmentPreview = None
        JobCostDetailAttachmentFiles =
          match attachmentIdOpt with
          | Some attachment ->
            match Map.tryFind attachment.Id model.JobCostDetailAttachmentFiles with
            | None
            | Some (VistaNotAvailable _) -> Map.remove attachment.Id model.JobCostDetailAttachmentFiles
            | Some _ -> model.JobCostDetailAttachmentFiles
          | None -> model.JobCostDetailAttachmentFiles },
    Cmd.none
  | CostOfConstructionPhaseExpansionToggled (contractId, phaseId) ->
    
    let phases =
      model.ExpandedCostOfConstructionPhases
      |> Map.tryFind contractId
      |> Option.defaultValue []
      |> fun phases -> match List.contains phaseId phases with
                       | true -> List.filter (fun x -> x <> phaseId) phases
                       | false -> phaseId :: phases
      |> fun phases -> Map.add contractId phases model.ExpandedCostOfConstructionPhases

    { model with ExpandedCostOfConstructionPhases = phases }, Cmd.none
  | CostOfConstructionDetailsExpansion request ->
    let details =
      match request with
      | Expand costId ->
        model.ExpandedCostOfConstructionDetails
        |> Map.add costId ()
      | Collapse costId ->
        model.ExpandedCostOfConstructionDetails
        |> Map.remove costId

    { model with ExpandedCostOfConstructionDetails = details },
    match request with
    | Expand costId ->
      if not <| Map.containsKey costId model.JobCostDetails then
        Cmd.ofMsg (ExecuteCostDetailSearch(costId, Search))
      else
        Cmd.none
    | _ -> Cmd.none
  | RequestPurchaseOrderContractId contractIdOpt ->
    if Option.isSome contractIdOpt then
      Util.bodyScrollDisable ()
    else
      Util.bodyScrollEnable ()

    { model with
        PurchaseOrderRequest =
          contractIdOpt |>>
            (fun contractId ->
              { ContractId = contractId
                Description = ""
                Items = ""
                Vendor = ""
                Location = ""
                Date = DateTime.Now }) },
    Cmd.none
  
  | PurchaseOrderRequestChanged purchaseOrderRequest ->
    { model with PurchaseOrderRequest = purchaseOrderRequest }, Cmd.none
  
  | SendPurchaseOrderRequest purchaseOrderRequestOpt ->
    match purchaseOrderRequestOpt with
    | Some request ->
      { request with Date = request.Date.ToUniversalTime() }
      |> RSSendPurchaseOrderRequest
      |> Bridge.Send
    | None -> ()
  
    { model with PurchaseOrderRequest = None }, Cmd.none
